import { readSync, readFileSync } from 'node:fs';
import { CliError, ExitCode, InvalidArgumentError } from './errors';

/**
 * Reject with `CliError(ServerOrNetworkError)` if a promise doesn't settle within `ms`.
 *
 * Reserved for async user / network input paths that v0.2 will add (streaming jobs,
 * interactive prompts with deadlines). Existing call sites either use their own
 * mechanism (`api-client` uses `AbortSignal.timeout`, `reset.ts` uses an `'end'`
 * handler) or are synchronous (`readFileSync(0)`). Removing this until then would
 * just churn the import graph; keeping it documents the contract.
 *
 * @internal — exported for future call sites, not part of public CLI API
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const t = setTimeout(() => {
			reject(new CliError(`Timed out after ${ms}ms: ${label}`, ExitCode.ServerOrNetworkError));
		}, ms);
		promise.then(
			(v) => {
				clearTimeout(t);
				resolve(v);
			},
			(err) => {
				clearTimeout(t);
				reject(err);
			}
		);
	});
}

/** Convenience: throw InvalidArgumentError on TTY stdin reads with no piped input. */
export function ensureStdinPiped(label: string): void {
	if (process.stdin.isTTY) {
		throw new InvalidArgumentError(`${label}: no data on stdin (run via pipe, or pass the value as an arg)`);
	}
}

/** Standalone backstop when a caller can't supply a `--timeout`-derived bound. */
const DEFAULT_STDIN_TIMEOUT_MS = 30_000;

export interface ReadPipedStdinOptions {
	/**
	 * Upper bound (ms) on total time spent waiting for a slow / hung upstream pipe.
	 * Callers pass the resolved `--timeout` so the limit is user-tunable; falls back to
	 * DEFAULT_STDIN_TIMEOUT_MS when omitted.
	 */
	timeoutMs?: number;
	/** Injectable clock for tests; defaults to `Date.now`. */
	now?: () => number;
}

/**
 * Synchronously drain piped stdin to a string, tolerating EAGAIN.
 *
 * A single `readFileSync(0)` / `readSync` can throw EAGAIN when stdin is a non-blocking
 * pipe whose upstream process is still producing (e.g. `wport jobs search ... | jq ... |
 * wport jobs view - --batch`): the fd has no data *right now* but isn't at EOF either.
 * Naively letting that throw makes the documented pipe workflow fail intermittently. We
 * retry on EAGAIN with a ~1ms synchronous sleep (Atomics.wait, to avoid a hot spin) and
 * stop on a zero-byte read or EOF.
 *
 * If the upstream neither produces data nor closes the pipe, the EAGAIN retry would spin
 * forever (the global `--timeout` only bounds HTTP, not stdin). We cap the total wait with
 * `timeoutMs` and surface a timeout as InvalidArgumentError rather than hanging silently.
 */
export function readPipedStdin(label: string, options: ReadPipedStdinOptions = {}): string {
	const timeoutMs = options.timeoutMs ?? DEFAULT_STDIN_TIMEOUT_MS;
	const now = options.now ?? Date.now;
	ensureStdinPiped(label);
	const chunks: Buffer[] = [];
	const buf = Buffer.alloc(64 * 1024);
	const sleeper = new Int32Array(new SharedArrayBuffer(4));
	const deadline = now() + timeoutMs;
	for (;;) {
		let bytesRead: number;
		try {
			bytesRead = readSync(0, buf, 0, buf.length, null);
		} catch (err) {
			const code = (err as NodeJS.ErrnoException).code;
			if (code === 'EAGAIN') {
				if (now() > deadline) {
					throw new InvalidArgumentError(`${label}: timed out after ${timeoutMs}ms waiting for piped stdin`);
				}
				Atomics.wait(sleeper, 0, 0, 1); // sleep ~1ms, then retry
				continue;
			}
			if (code === 'EOF') break;
			throw new InvalidArgumentError(`${label}: failed to read stdin: ${(err as Error).message}`);
		}
		if (bytesRead === 0) break;
		chunks.push(Buffer.from(buf.subarray(0, bytesRead)));
	}
	return Buffer.concat(chunks).toString('utf8');
}

export interface PromptSecretOptions {
	/** 注入點，預設用本檔的 readPipedStdin（測試可換 fake）。 */
	readPiped?: (label: string) => string;
}

/**
 * 互動式讀 secret：TTY 時 raw mode 隱藏輸入（不 echo、不進 shell history）；
 * 非 TTY（CI / pipe）時直接讀整個 stdin。供 `wport enterprise login` 用。
 */
export function promptSecret(promptText: string, options: PromptSecretOptions = {}): Promise<string> {
	const readPiped = options.readPiped ?? ((label: string) => readPipedStdin(label));
	if (!process.stdin.isTTY || !process.stdout.isTTY) {
		return Promise.resolve(readPiped('login').trim());
	}
	process.stdout.write(promptText);
	return new Promise<string>((resolve, reject) => {
		const stdin = process.stdin;
		stdin.setRawMode(true);
		stdin.resume();
		stdin.setEncoding('utf8');
		let buf = '';
		const cleanup = (): void => {
			stdin.setRawMode(false);
			stdin.pause();
			stdin.off('data', onData);
		};
		const onData = (chunk: string): void => {
			for (const ch of chunk) {
				if (ch === '') {
					// Ctrl-C
					cleanup();
					process.stdout.write('\n');
					reject(new CliError('Aborted', ExitCode.InvalidArgument));
					return;
				}
				if (ch === '\r' || ch === '\n') {
					cleanup();
					process.stdout.write('\n');
					resolve(buf.trim());
					return;
				}
				if (ch === '' || ch === '\b') {
					buf = buf.slice(0, -1);
					continue;
				}
				buf += ch;
			}
		};
		stdin.on('data', onData);
	});
}

export interface ReadJsonInputOptions {
	/** 傳給 readPipedStdin 的上限（通常帶 resolved --timeout）。 */
	timeoutMs?: number;
	/** 注入點，預設用 readPipedStdin；測試可換 fake，避免真的讀 fd 0。 */
	readStdin?: (label: string) => string;
}

/**
 * 讀 `--file <path>` 的 JSON body 供寫入命令（jobs create/update/batch）使用。
 * `path === '-'` 讀 stdin（管線 / here-doc）。body 直送後端驗證，CLI 只負責讀取 + parse。
 *
 * 讀不到檔 / 空輸入 / JSON parse 失敗 → InvalidArgumentError（exit 2、不發請求），
 * 錯誤訊息不含檔案內容（可能含敏感資料）。
 */
export function readJsonInput(source: string, options: ReadJsonInputOptions = {}): unknown {
	const readStdin = options.readStdin ?? ((label: string) => readPipedStdin(label, { timeoutMs: options.timeoutMs }));
	let raw: string;
	if (source === '-') {
		raw = readStdin('--file -');
	} else {
		try {
			raw = readFileSync(source, 'utf8');
		} catch (err) {
			throw new InvalidArgumentError(
				`Cannot read --file "${source}": ${(err as NodeJS.ErrnoException).code ?? (err as Error).message}`
			);
		}
	}
	if (!raw.trim()) {
		throw new InvalidArgumentError('Input is empty — expected a JSON body');
	}
	try {
		return JSON.parse(raw);
	} catch {
		// 不夾帶 err.message：Node 的 JSON.parse SyntaxError 會把輸入片段放進訊息，
		// 可能外洩 --file 內容（如 secret）。固定訊息，守住上面 docstring 的承諾。
		throw new InvalidArgumentError('Invalid JSON in input (parse failed)');
	}
}

/**
 * 讀 `--body-file <path>` 之類的純文字輸入（respond 信件內文）。
 * 結構鏡射 readJsonInput（source==='-' 讀 stdin，否則 readFileSync），但**回傳原始字串、不 parse**。
 * 讀不到檔 / 空輸入 → InvalidArgumentError（exit 2、不發請求）；錯誤訊息不含檔案內容。
 */
export function readTextInput(source: string, options: ReadJsonInputOptions = {}): string {
	const readStdin = options.readStdin ?? ((label: string) => readPipedStdin(label, { timeoutMs: options.timeoutMs }));
	let raw: string;
	if (source === '-') {
		raw = readStdin('--body-file -');
	} else {
		try {
			raw = readFileSync(source, 'utf8');
		} catch (err) {
			throw new InvalidArgumentError(
				`Cannot read --body-file "${source}": ${(err as NodeJS.ErrnoException).code ?? (err as Error).message}`
			);
		}
	}
	if (!raw.trim()) {
		throw new InvalidArgumentError('Input is empty — expected message body text');
	}
	return raw;
}

/**
 * 同 readJsonInput，但要求 parse 結果是 JSON 物件（非陣列 / 非純量）。
 * jobs create/update 的 body、batch 的 `{jobs:[...]}` 外層皆為物件；先本地擋，錯誤更清楚。
 */
export function readJsonObject(source: string, options: ReadJsonInputOptions = {}): Record<string, unknown> {
	const parsed = readJsonInput(source, options);
	if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
		throw new InvalidArgumentError(
			`Input must be a JSON object, got ${Array.isArray(parsed) ? 'an array' : typeof parsed}`
		);
	}
	return parsed as Record<string, unknown>;
}
